import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";

// Deep-merge defaults with stored settings
function deepMerge(base, override) {
  if (!override) return base;
  const result = { ...base };
  for (const k of Object.keys(override)) {
    if (override[k] !== null && typeof override[k] === "object" && !Array.isArray(override[k]) && typeof result[k] === "object" && !Array.isArray(result[k])) {
      result[k] = deepMerge(result[k], override[k]);
    } else if (override[k] !== undefined && override[k] !== null) {
      result[k] = override[k];
    }
  }
  return result;
}

const DEFAULTS = {
  site_name: "ScrapeCore",
  site_tagline: "Enterprise web intelligence platform.",
  accent_color: "#00ff88",
  hero: {
    badge: "// 13-tier cascade engine",
    title: "Scrape Any Website.",
    title_highlight: "Beat Any Bot Wall.",
    subtitle: "Cloudflare, Kasada, Akamai bypass. Twitter / X.com data included.",
    cta_primary_text: "$ start free",
    cta_primary_href: "/register",
    cta_secondary_text: "view docs",
    cta_secondary_href: "/docs",
    inline_stats: ["99.2% uptime", "50ms avg response", "13 bypass tiers"],
  },
  stats_bar: [
    { value: "2.4M+", label: "requests / month" },
    { value: "50ms",  label: "avg response time" },
    { value: "99.2%", label: "uptime SLA" },
    { value: "13",    label: "bypass tiers" },
  ],
  nav_links: [
    { label: "features",  href: "#features" },
    { label: "cascade",   href: "#cascade" },
    { label: "benchmark", href: "#benchmark" },
    { label: "pricing",   href: "#pricing" },
    { label: "docs",      href: "/docs" },
  ],
  final_cta: {
    badge: "// ready",
    title: "Ready to scrape?",
    subtitle: "1,000 free credits. No credit card. Start in 60 seconds.",
    cta_primary_text: "$ start free",
    cta_primary_href: "/register",
    cta_secondary_text: "read the docs",
    cta_secondary_href: "/docs",
  },
  footer: {
    tagline: "Enterprise web intelligence platform. 13-tier cascade engine.",
    github_url: "https://github.com",
    copyright: "© 2026 ScrapeCore",
    bottom_links: [
      { label: "Privacy",  href: "/privacy" },
      { label: "Terms",    href: "/terms" },
      { label: "Contact",  href: "/contact" },
    ],
    link_groups: [
      {
        heading: "Product",
        links: [
          { label: "Features",  href: "#features" },
          { label: "Pricing",   href: "#pricing" },
          { label: "Docs",      href: "/docs" },
          { label: "Blog",      href: "/blog" },
        ],
      },
      {
        heading: "Legal",
        links: [
          { label: "Privacy Policy",  href: "/privacy" },
          { label: "Terms of Service", href: "/terms" },
          { label: "Refund Policy",   href: "/cancellation-refund-policy" },
        ],
      },
    ],
  },
  faq: [],
  verification: { head_html: "", body_start_html: "", body_end_html: "" },
};

let _cached = null;
let _fetchPromise = null;

export function useSiteSettings() {
  const [settings, setSettings] = useState(_cached || DEFAULTS);
  const [loading, setLoading]   = useState(!_cached);

  useEffect(() => {
    if (_cached) { setSettings(_cached); setLoading(false); return; }
    if (_fetchPromise) {
      _fetchPromise.then(s => { setSettings(s); setLoading(false); });
      return;
    }
    _fetchPromise = api.get("/site-settings")
      .then(r => {
        const merged = deepMerge(DEFAULTS, r.data);
        _cached = merged;
        return merged;
      })
      .catch(() => DEFAULTS)
      .finally(() => { _fetchPromise = null; });
    _fetchPromise.then(s => { setSettings(s); setLoading(false); });
  }, []);

  return { settings, loading };
}

export function invalidateSiteSettingsCache() {
  _cached = null;
}
